import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import LoadContentSpinner from '../../components/Utils/LoadContentSpinner';
import QrCodeSection from '../../components/QrCodeSection';
import { apiUrl } from '../../config/site';
import { Info, HelpCircle, Smartphone, AlertTriangle, Monitor, ChevronUp, ChevronDown } from "lucide-react"

const STORE_LINKS = {
    ios: 'https://apps.apple.com/ci/app/events-billetterie/id6738791983',
    android: 'https://play.google.com/store/apps/details?id=com.event.event_app',
};

const APP_DEEP_LINKS = {
    ios: process.env.REACT_APP_IOS_APP_LINK || 'events://eventsbilletterie.com',
    android: process.env.REACT_APP_ANDROID_APP_LINK || 'events://eventsbilletterie.com',
};

const FALLBACK_DELAY = 1500;

const tryOpenAppOrStore = ({ appLink, storeLink }) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return () => {};
    }

    if (!appLink) {
        window.location.href = storeLink;
        return () => {};
    }

    let fallbackTimer;
    let hasNavigated = false;
    let cleanup;

    const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            hasNavigated = true;
            cleanup();
        }
    };

    cleanup = () => {
        if (fallbackTimer) {
            window.clearTimeout(fallbackTimer);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    fallbackTimer = window.setTimeout(() => {
        if (hasNavigated) return;
        cleanup();
        window.location.href = storeLink;
    }, FALLBACK_DELAY);

    window.location.href = appLink;

    return cleanup;
};

const SmartLinkRedirect = () => {

    const [showDefault, setShowDefault] = useState(false);
    // État pour contrôler l'affichage de la section explicative
    const [isExplanationOpen, setIsExplanationOpen] = useState(false)
    // État pour stocker les informations sur l'appareil
    const [deviceInfo, setDeviceInfo] = useState({
        name: "Appareil inconnu",
        os: null,
        isIOS: false,
        isAndroid: false,
    })

    const detectDevice = () => {
        const ua = navigator.userAgent || '';
        const isIOS = /iPhone|iPad|iPod/i.test(ua);
        const isAndroid = /Android/i.test(ua);

        let os = "Système inconnu";
        let deviceName = "Appareil inconnu";

        if (isIOS) {
            os = "iOS";
            deviceName = "Appareil Apple";
        } else if (isAndroid) {
            os = "Android";
            deviceName = "Appareil Android";
        } else if (/windows phone/i.test(ua)) {
            os = "Windows Phone";
            deviceName = "Windows Phone";
        } else if (/windows|macintosh|linux/i.test(ua)) {
            os = /windows/i.test(ua) ? "Windows" : /macintosh/i.test(ua) ? "macOS" : "Linux";
            deviceName = `Ordinateur ${os}`;
        }

        const detectedDeviceInfo = {
            name: deviceName,
            os,
            isIOS,
            isAndroid,
        };

        setDeviceInfo(detectedDeviceInfo);
        return detectedDeviceInfo; // 👈 Retourne les données immédiatement
    };

    // Nouvelle fonction de tracking utilisant sendBeacon
    const trackClick = (platform, deviceInfoToSend) => {
        const url = apiUrl('tracking');
        const data = JSON.stringify({
            platform,
            device_info: deviceInfoToSend.name,
            tracking_type: 'smartlink_redirect',
        });
        const blob = new Blob([data], { type: 'application/json' });
        if (navigator.sendBeacon) {
            navigator.sendBeacon(url, blob);
        } else {
            // Fallback si sendBeacon n'est pas supporté
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', },
                body: data,
                keepalive: true,
            });
        }
    };

    useEffect(() => {
        const detectedDeviceInfo = detectDevice();
        let cleanupHandler;

        if (detectedDeviceInfo.isIOS) {
            trackClick('iOS', detectedDeviceInfo);
            cleanupHandler = tryOpenAppOrStore({
                appLink: APP_DEEP_LINKS.ios,
                storeLink: STORE_LINKS.ios,
            });
        } else if (detectedDeviceInfo.isAndroid) {
            trackClick('Android', detectedDeviceInfo);
            cleanupHandler = tryOpenAppOrStore({
                appLink: APP_DEEP_LINKS.android,
                storeLink: STORE_LINKS.android,
            });
        } else {
            trackClick('Other', detectedDeviceInfo);
            const timer = window.setTimeout(() => setShowDefault(true), 100);
            cleanupHandler = () => window.clearTimeout(timer);
        }

        return () => {
            if (cleanupHandler) {
                cleanupHandler();
            }
        };
    }, []);

    // Fonction pour basculer l'affichage de la section explicative
    const toggleExplanation = () => {
        setIsExplanationOpen(!isExplanationOpen)
    }

    return (
        <>
            {/* Balises SEO */}
            <Helmet>
                <title>Téléchargez notre application mobile | Events</title>
                <meta name="description" content="Découvrez notre application mobile Events billetterie disponible sur iOS et Android." />
                <meta 
                    name="keywords" 
                    content="Events, application événements Côte d'Ivoire, télécharger application billetterie, billetterie événementielle Afrique, réservez vos tickets en ligne, application événements Abidjan, smartlink Events, application mobile événements, acheter billets événements CI, application Events Android, application Events iOS, téléchargement application événements, plateforme billetterie mobile, Events billetterie, billetterie, ticket événement" />
                <meta property="og:title" content="Téléchargez notre application mobile Events billetterie" />
                <meta property="og:description" content="Disponible sur iOS et Android – Téléchargez l'application Events billetterie dès maintenant." />
                <meta property="og:image" content='https://eventsbilletterie.com/assets/images/facadeEvents.png' />
                <meta property="og:url" content="https://eventsbilletterie.com/go" />
                <meta property="og:type" content="website" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Téléchargez notre application mobile Events billetterie" />
                <meta name="twitter:description" content="Disponible sur iOS et Android – Téléchargez l'application Events billetterie dès maintenant." />
                <meta name="twitter:image" content='https://eventsbilletterie.com/assets/images/facadeEvents.png' />
                <meta name="twitter:site" content="@events_billetterie" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://eventsbilletterie.com/go" />
            </Helmet>
            <div className='container'>
                {showDefault ? (
                    <>
                        <QrCodeSection />

                        {/* Sections d'information avec design simplifié */}
                        <div className="container max-w-3xl mx-auto mb-12 space-y-6">
                            {/* Section d'information sur l'appareil - Design simple */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-start gap-4 mb-4">
                                    <Smartphone className="h-4 w-4 text-gray-500 mt-0.5" />
                                    <h3 className="text-lg font-semibold text-gray-800">Informations sur votre appareil</h3>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-3">
                                    {/* Appareil */}
                                    <div className="flex items-start gap-3">
                                        <Monitor className="h-4 w-4 text-gray-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs text-gray-500">Appareil</p>
                                            <p className="text-gray-800 text-sm">
                                                {deviceInfo.name || "Non détecté"}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Système */}
                                    <div className="flex items-start gap-3">
                                        <Info className="h-4 w-4 text-gray-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs text-gray-500">Système</p>
                                            <p className="text-gray-800 text-sm">{deviceInfo.os || "Inconnu"}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="w-14 py-2 border-b border-neutral-200 dark:border-neutral-700" />
                                {/* Statut de compatibilité */}
                                <div className="flex items-start gap-3 pt-2">
                                    
                                    <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                                    <div>
                                        <p className="text-gray-700 text-sm">
                                            L'application Events n'est pas disponible pour votre appareil actuel.
                                        </p>
                                    </div>
                                </div>
                                {/* Bouton pour afficher/masquer l'explication */}
                                <button
                                    onClick={toggleExplanation}
                                    className="flex items-center gap-2 mt-4 text-sm text-blue-500 hover:text-blue-700 transition-colors"
                                >
                                    {isExplanationOpen ? (
                                    <>
                                        <span>Masquer les détails</span>
                                        <ChevronUp className="h-4 w-4" />
                                    </>
                                    ) : (
                                    <>
                                        <span>Pourquoi voyez-vous cette page ?</span>
                                        <ChevronDown className="h-4 w-4" />
                                    </>
                                    )}
                                </button>
                                {/* Section d'explication - Design simple */}
                                <div 
                                    className={`bg-gradient-to-r from-[#5bcfc5]/10 to-[#5bcfc5]/5 rounded-xl pt-3 px-4 mt-3 border overflow-hidden transition-all duration-300 ease-in-out ${
                                    isExplanationOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0 py-0 px-0 mt-0 border-0"
                                  }`}
                                >
                                    <div className={`transition-opacity duration-300 ${isExplanationOpen ? "opacity-100" : "opacity-0"}`}>

                                        <div className="flex items-start gap-4 mb-4">
                                            <HelpCircle className="h-4 w-4 text-gray-500 mt-0.5" />
                                            <h3 className="text-lg font-semibold text-gray-800">Pourquoi voyez-vous cette page ?</h3>
                                        </div>

                                        <div className="pl-9 pb-4 text-sm">
                                            <div className="space-y-3">
                                                <p className="text-gray-700">
                                                Nous détectons automatiquement l'appareil que vous utilisez et vous redirigeons vers la bonne boutique d'applications.
                                                </p>
                                                <p className="text-gray-700">
                                                Cependant, nous n'avons pas pu trouver un lien de téléchargement pour votre appareil {deviceInfo.os}.
                                                L'application {'Events'} est disponible uniquement sur les appareils iOS et Android.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className='container py-16 lg:py-15 space-y-16 lg:space-y-28'>
                        <div className="flex justify-center items-center text-center flex-col">
                            <LoadContentSpinner />
                            <h2 className="text-lg font-bold mt-5">Redirection en cours...</h2>
                            <p className='text-gray-600 pt-2 text-sm dark:text-neutral-300'>
                                Si vous n'êtes pas redirigé automatiquement,{' '}
                                <a className='text-primary-6000 font-semibold' href="/">cliquez ici</a>.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default SmartLinkRedirect;